import { useState, useEffect, Fragment } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { Globe, Key, Shield, Sparkles, Clock, Zap, Search, EyeOff, BookOpen, Download, Check, Gift, Lock, Film, ArrowRight, Users, Calculator, Building2, Banknote, Mail, Plane, MapPin, Youtube, Instagram, Facebook } from "lucide-react";

// Components
import { Header } from "./components/Header";
import { PlatformCard } from "./components/PlatformCard";
import { CredentialDialog } from "./components/CredentialDialog";
import { SecretoSection } from "./components/SecretoSection";
import CompPlanVideo from "./components/CompPlanVideo";
import BrandsStrip from "./components/BrandsStrip";
import { ClubViajemosLogoFull, ClubViajemosText } from "./components/ClubViajemosLogo";
import { TutorialsSection } from "./components/TutorialsSection";
import { EventsSection } from "./components/EventsSection";
import HomeHero from "./components/HomeHero";
import HomePriceVideoSection from "./components/HomePriceVideoSection";
import AwardShowcase from "./components/AwardShowcase";
import { VIPAccessPage } from "./components/VIPAccessPage";
import AdminPage from "./components/AdminPage";
import CertificadosPage from "./components/CertificadosPage";
import RedimirPage from "./components/RedimirPage";
import PaymentLinksPage from "./components/PaymentLinksPage";
import LicenciasPage from "./components/LicenciasPage";
import { FdacsViewerPage, BondViewerPage, FloridaCertificateViewerPage } from "./components/DocViewerPage";
import VideosQueHacenDinero from "./components/VideosQueHacenDinero";
import { AAHeaderBanner, AAFooterBanner } from "./components/branding/AABanners";
import Protocolo2026Page from "./components/Protocolo2026Page";
import CursoVideosPage from "./components/CursoVideosPage";
import AnalisisFinancieroPage from "./components/AnalisisFinancieroPage";
import AnalisisFinancieroLanding from "./components/AnalisisFinancieroLanding";
import CertificadosSimuladorPage from "./components/CertificadosSimuladorPage";
import ClubVacacionalSimuladorPage from "./components/ClubVacacionalSimuladorPage";
import LanguagePromptBanner from "./components/LanguagePromptBanner";
import InversoresRedirect from "./components/InversoresRedirect";
import SociosFundadoresRedirect from "./components/SociosFundadoresRedirect";
import KitInversorRedirect from "./components/KitInversorRedirect";
import CursoClubVacacionalRedirect from "./components/CursoClubVacacionalRedirect";
import CursoVisaConExitoRedirect from "./components/CursoVisaConExitoRedirect";
import CursoElPaqueteDelMillonRedirect from "./components/CursoElPaqueteDelMillonRedirect";
import CursoReferidosRedirect from "./components/CursoReferidosRedirect";
import CursoAcademiaB2BRedirect from "./components/CursoAcademiaB2BRedirect";
import AcademiaPage from "./components/AcademiaPage";
import SalesFirstPage from "./components/salesfirst/SalesFirstPage";
import HotelPromoPage from "./components/HotelPromoPage";
import EvolucionSistemaPage from "./components/EvolucionSistemaPage";
import OpcPage from "./components/OpcPage";
import TonsOfLeadsPage from "./components/TonsOfLeadsPage";
import BusinessReliefRedirect from "./components/BusinessReliefRedirect";
import OpcTerminosPage from "./components/OpcTerminosPage";
import CenaConCarlosRedirect from "./components/CenaConCarlosRedirect";
import RegistrosOpcRedirect from "./components/RegistrosOpcRedirect";
import CursoVideoAcademyRedirect from "./components/CursoVideoAcademyRedirect";
import CursoCierreMaestroRedirect from "./components/CursoCierreMaestroRedirect";
import CursoKitArranqueRedirect from "./components/CursoKitArranqueRedirect";
import ComoGanarDineroPage from "./components/ComoGanarDineroPage";
import VendeConIaPage from "./components/VendeConIaPage";
import ProspeccionDigitalPage from "./components/ProspeccionDigitalPage";
import Manual27Page from "./components/Manual27Page";
import Manual27SuccessPage from "./components/Manual27SuccessPage";
import ManualCompletoPage from "./components/ManualCompletoPage";
import HistoriaEnVideosPage from "./components/HistoriaEnVideosPage";
import OrlandoMiamiRedirect from "./components/OrlandoMiamiRedirect";
import CalculadoraRedirect from "./components/CalculadoraRedirect";
import TerminosPage from "./components/TerminosPage";
import PrivacidadPage from "./components/PrivacidadPage";
import AereosLandingPage from "./components/AereosLandingPage";
import SetterScriptsPage from "./components/SetterScriptsPage";
import VentaDirectaScriptPage from "./components/VentaDirectaScriptPage";
import HomeDemoPage from "./components/HomeDemoPage";
import TelepromptrPage from "./components/TelepromptrPage";
import PlanCompensacionRedirect from "./components/PlanCompensacionRedirect";
import AvisoCodigosPage from "./components/AvisoCodigosPage";
import FloatingWhatsApp from "./components/FloatingWhatsApp";
import BackgroundMusic from "./components/BackgroundMusic";
import SpaceRedirect from "./components/SpaceRedirect";
import LeadsRedirect from "./components/LeadsRedirect";
import TrainingRedirect from "./components/TrainingRedirect";
import VslRedirect from "./components/VslRedirect";

import PlanesEmbed from "./components/PlanesEmbed";
import EducacionPage from "./components/EducacionPage";

import { Button } from "./components/ui/button";
import { downloadFile } from "./utils/downloadFile";
import { ytEmbedUrl } from "./utils/youtube";

// Hooks
import useCredentials from "./hooks/useCredentials";
import useTheme from "./hooks/useTheme";

// Platform Data
const SECRETOS_DATA = [
  {
    name: "Secreto #1",
    platforms: ["www.certiviaje.com", "www.bombazoo.com"]
  },
  {
    name: "Secreto #2",
    platforms: ["www.dividelo.com"]
  },
  {
    name: "Secreto #3",
    platforms: ["www.ratehawk.com"]
  },
  {
    name: "Secreto #4",
    platforms: ["www.clubviajemos.us"]
  },
  {
    name: "Secreto #5",
    platforms: ["www.hotmart.com"]
  }
];

// Special Platform - Organizer
const ORGANIZER_PLATFORM = {
  name: "Tu Organizador de Tiempos y Prospección",
  description: "El sistema que divide tu día en bloques de 15 minutos con tareas exactas por pilares de negocio. Sin decisiones, solo ejecución.",
  platform: "www.viajemoscorp.com"
};

import { API_URL } from './config/api';

const Dashboard = () => {
  const { isDark, toggleTheme } = useTheme();
  const { 
    updatePlatformCredentials, 
    getPlatformCredentials, 
    hasCredentials,
    clearPlatformCredentials,
    exportCredentials,
    importCredentials
  } = useCredentials();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [tutorials, setTutorials] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [presentationMode, setPresentationMode] = useState(false);
  
  // Load tutorials from backend (with retry to survive Cloudflare 520s)
  useEffect(() => {
    const loadTutorials = async () => {
      const MAX_RETRIES = 3;
      for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
        try {
          const response = await fetch(`${API_URL}/api/tutorials`, { cache: 'no-store' });
          if (response.ok) {
            const data = await response.json();
            setTutorials(data);
            return;
          }
          if (response.status < 500) break;
        } catch (error) {
          console.error(`Tutorials attempt ${attempt + 1} failed:`, error);
        }
        if (attempt < MAX_RETRIES - 1) {
          await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
        }
      }
    };
    loadTutorials();
  }, []); // Load once on mount

  // Group tutorials by secreto - no longer needed per section
  // Tutorials will be shown in a single section at the end

  const handleEditPlatform = (platform) => {
    setSelectedPlatform(platform);
    setDialogOpen(true);
  };

  const handleSaveCredentials = (platform, username, password, links, notes) => {
    updatePlatformCredentials(platform, username, password, links, notes);
  };

  const handleDeleteCredentials = (platform) => {
    clearPlatformCredentials(platform);
  };

  // Count total platforms with saved credentials (including organizer)
  const totalPlatforms = SECRETOS_DATA.reduce((acc, s) => acc + s.platforms.length, 0) + 1;
  const savedCount = SECRETOS_DATA.reduce((acc, s) => 
    acc + s.platforms.filter(p => hasCredentials(p)).length, 0
  ) + (hasCredentials(ORGANIZER_PLATFORM.platform) ? 1 : 0);

  return (
    <div 
      data-testid="dashboard-container"
      className="min-h-screen bg-[#F8FAFC] dark:bg-[#0B0F19] transition-colors duration-300"
    >
      <BackgroundMusic />
      <Header
        isDark={isDark}
        toggleTheme={toggleTheme}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        onExport={exportCredentials}
        onImport={importCredentials}
        presentationMode={presentationMode}
        onTogglePresentationMode={() => setPresentationMode(!presentationMode)}
      />
      
      {/* Presentation Mode Banner */}
      {presentationMode && (
        <div data-testid="presentation-mode-banner" className="bg-[#45A0EE] text-white text-center py-2 px-4 flex items-center justify-center gap-2 text-sm font-medium">
          <EyeOff className="h-4 w-4" />
          Modo Presentación activo — Las credenciales están ocultas
        </div>
      )}
      
      {/* Hero Section - Award-winning refresh */}
      <HomeHero />

      {/* Precio de agencia + video (segunda sección) */}
      <HomePriceVideoSection />

      {/* Reconocimiento — primer impacto */}
      <AwardShowcase />


      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Events Section - Right after Hero */}
        <EventsSection />
        
        {/* Manual de Negocios - Acceso GRATIS (antes funnel $27) */}
        <section data-testid="manual-premium-section" className="mb-10">
          {/* Link to historia en videos */}
          <a
            href="/la-historia-en-videos"
            data-testid="cta-historia-videos"
            className="group mt-4 flex items-center justify-between gap-4 bg-gradient-to-r from-[#1f2a4f] via-[#2a3a6e] to-[#1f2a4f] hover:from-[#2a3a6e] hover:to-[#1f2a4f] border border-[#45A0EE]/30 hover:border-[#45A0EE]/60 rounded-xl px-5 py-4 transition-all shadow-lg hover:shadow-[0_10px_40px_rgba(69,160,238,0.2)]"
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-11 h-11 rounded-lg bg-[#45A0EE]/20 border border-[#45A0EE]/40 flex items-center justify-center flex-shrink-0">
                <Film className="h-5 w-5 text-[#45A0EE]" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] text-[#45A0EE] font-mono uppercase tracking-[2px] mb-0.5">BONUS VISUAL</p>
                <p className="text-white font-bold text-sm sm:text-base truncate">La Historia en Videos <span className="text-slate-400 font-normal">— 27 videos + avales de TV</span></p>
                <p className="text-slate-400 text-xs truncate hidden sm:block">Momentos, aprendizajes y respaldo mediatico</p>
              </div>
            </div>
            <span className="flex items-center gap-1 text-[#45A0EE] text-xs font-bold uppercase tracking-wider flex-shrink-0 group-hover:gap-2 transition-all">
              Ver <ArrowRight className="h-4 w-4" />
            </span>
          </a>
        </section>
        
        {/* Aerolíneas auspiciadoras */}

        {/* Casos de éxito de nuestros agentes — protagonistas */}
        <TutorialsSection tutorials={tutorials.filter((t) => (t.location || 'home') === 'home')} />

        {/* Plan de Compensación — video (reemplaza Secretos 1-5) */}
        <CompPlanVideo />

        {/* Trabajamos con las compañías de viajes más grandes del mundo */}
        <BrandsStrip />

      </main>

      {/* Correos de contacto */}
      <section data-testid="contact-emails-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-10">
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0a0a14] via-[#13132a] to-[#1a1a2e] border border-white/10 p-6 sm:p-8">
          <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-[#45A0EE]/15 blur-3xl pointer-events-none" />
          <div className="relative text-center mb-6">
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-4 py-1.5 mb-2">
              <Mail className="h-3.5 w-3.5 text-[#45A0EE]" />
              <span className="text-white text-[11px] font-black uppercase tracking-[2px]">Correos de contacto</span>
            </div>
            <p className="text-slate-400 text-sm max-w-xl mx-auto">
              Escríbenos al correo correcto según tu necesidad para una respuesta más rápida.
            </p>
          </div>

          <div className="relative grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { email: "info@clubviajemos.com", desc: "Certificados de Viaje, Visas, Tickets y Agencia de Viajes", accent: "#45A0EE" },
              { email: "carlos@clubviajemos.com", desc: "Si deseas ser Agente Travel Empire", accent: "#D4AF37" },
            ].map((c) => (
              <a
                key={c.email}
                href={`mailto:${c.email}`}
                data-testid={`contact-email-${c.email}`}
                className="group flex items-start gap-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl p-4 transition-all hover:-translate-y-0.5"
              >
                <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${c.accent}22` }}>
                  <Mail className="h-5 w-5" style={{ color: c.accent }} />
                </div>
                <div className="min-w-0">
                  <p className="text-white font-bold text-sm sm:text-base break-all group-hover:underline">{c.email}</p>
                  <p className="text-slate-400 text-xs sm:text-sm">{c.desc}</p>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* American Airlines — socio de vuelo (antes del cierre) */}
      <AAFooterBanner />

      {/* Footer */}
      <footer 
        data-testid="app-footer"
        className="border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-[#0B0F19]"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col items-center justify-center gap-4 text-center">
            <div className="flex items-center gap-3">
              <p className="text-sm font-semibold text-[#1D1D1B] dark:text-white">
                Club<span className="text-[#45A0EE]">Viajemos</span>
              </p>
              <span className="text-slate-300 dark:text-slate-600">|</span>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                © 2025 Travel Empire
              </p>
            </div>

            {/* Redes sociales */}
            <div className="flex items-center justify-center gap-5" data-testid="footer-social">
              <a href="https://www.youtube.com/@ClubViajemos" target="_blank" rel="noopener noreferrer" aria-label="YouTube" data-testid="social-youtube" className="transition-transform hover:scale-110">
                <img src="/social/youtube.svg" alt="YouTube" className="h-9 w-9 object-contain" />
              </a>
              <a href="https://www.instagram.com/carlosmiami.clubviajemos/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="social-instagram" className="transition-transform hover:scale-110">
                <img src="/social/instagram.svg" alt="Instagram" className="h-9 w-9 object-contain" />
              </a>
              <a href="https://www.tiktok.com/@clubviajemos" target="_blank" rel="noopener noreferrer" aria-label="TikTok" data-testid="social-tiktok" className="transition-transform hover:scale-110">
                <img src="/social/tiktok.png" alt="TikTok" className="h-9 w-9 object-contain" />
              </a>
              <a href="https://www.facebook.com/carlos.fernandez.817906" target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="social-facebook" className="transition-transform hover:scale-110">
                <img src="/social/facebook.webp" alt="Facebook" className="h-9 w-9 object-contain" />
              </a>
            </div>
            <p className="text-xs text-slate-400 dark:text-slate-500 text-center flex flex-wrap justify-center items-center gap-x-1 gap-y-1">
              <a href="/paylinks.html" data-testid="footer-link-paylinks" className="hover:text-[#635BFF] transition-colors font-semibold">PAYLINKS</a>
              <span className="mx-2">·</span>
              <a href="/licencias" data-testid="footer-link-licencias" className="hover:text-[#45A0EE] transition-colors font-semibold">LICENCIAS</a>
              <span className="mx-2">·</span>
              <a href="/libro.html" data-testid="footer-link-libro" className="hover:text-[#E0B341] transition-colors font-semibold">LIBRO</a>
              <span className="mx-2">·</span>
              <a href="/academia" data-testid="footer-link-universidad" className="hover:text-[#80C753] transition-colors font-semibold">UNIVERSIDAD</a>
              <span className="mx-2">·</span>
              <a href="/analisisfinanciero" data-testid="footer-link-cuanto-gano" className="hover:text-[#80C753] transition-colors font-semibold">¿Cuánto puedo ganar?</a>
              <span className="mx-2">·</span>
              <a href="/plandeagencias" data-testid="footer-link-precios" className="hover:text-[#45A0EE] transition-colors">Precios</a>
              <span className="mx-2">·</span>
              <a href="/terminos" data-testid="footer-link-terminos" className="hover:text-[#45A0EE] transition-colors">Términos de Servicio</a>
              <span className="mx-2">·</span>
              <a href="/privacidad" data-testid="footer-link-privacidad" className="hover:text-[#45A0EE] transition-colors">Política de Privacidad</a>
            </p>
          </div>
        </div>
      </footer>

      {/* Credential Dialog */}
      <CredentialDialog
        isOpen={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setSelectedPlatform(null);
        }}
        platform={selectedPlatform}
        currentCredentials={selectedPlatform ? getPlatformCredentials(selectedPlatform) : null}
        onSave={handleSaveCredentials}
        onDelete={handleDeleteCredentials}
      />

      {/* Toast Notifications */}
      <Toaster 
        position="bottom-right"
        toastOptions={{
          style: {
            background: isDark ? '#151B2B' : '#FFFFFF',
            border: isDark ? '1px solid #334155' : '1px solid #E2E8F0',
            color: isDark ? '#FFFFFF' : '#1D1D1B',
          },
        }}
      />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ConditionalLangBanner />
        <ConditionalAAHeader />
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/space" element={<SpaceRedirect />} />
          <Route path="/leads" element={<LeadsRedirect />} />
          <Route path="/training" element={<TrainingRedirect />} />
          <Route path="/tus25000dolares" element={<VslRedirect />} />
          <Route path="/promo" element={<VIPAccessPage promoMode />} />
          <Route path="/vip-access" element={<VIPAccessPage />} />
          <Route path="/plandeagencias" element={<VIPAccessPage />} />
          <Route path="/planes" element={<PlanesEmbed />} />
          <Route path="/educacion" element={<EducacionPage />} />
          <Route path="/comoseragente" element={<Navigate to="/plandeagencias" replace />} />
          <Route path="/precios" element={<Navigate to="/plandeagencias" replace />} />
          <Route path="/administracionglobal" element={<AdminPage />} />
          <Route path="/codigosdeviaje500dolares" element={<CertificadosPage />} />
          <Route path="/redimir" element={<RedimirPage />} />
          {/* Premium gated content (post $27 purchase) */}
          <Route path="/manual-completo" element={<ManualCompletoPage />} />
          <Route path="/comoganardinero" element={<ComoGanarDineroPage />} />
          <Route path="/vendeconia" element={<VendeConIaPage />} />
          <Route path="/prospecciondigital" element={<ProspeccionDigitalPage />} />
          <Route path="/manual27" element={<Navigate to="/" replace />} />
          <Route path="/manual27/exito" element={<Navigate to="/" replace />} />
          <Route path="/la-historia-en-videos" element={<HistoriaEnVideosPage />} />
          <Route path="/orlandomiami" element={<OrlandoMiamiRedirect />} />
          <Route path="/calculadora" element={<CalculadoraRedirect />} />
          <Route path="/terminos" element={<TerminosPage />} />
          <Route path="/tos" element={<TerminosPage />} />
          <Route path="/privacidad" element={<PrivacidadPage />} />
          <Route path="/privacy" element={<PrivacidadPage />} />
          <Route path="/aereos" element={<AereosLandingPage />} />
          <Route path="/licencias" element={<LicenciasPage />} />
          <Route path="/licencias/fdacs" element={<FdacsViewerPage />} />
          <Route path="/licencias/bond" element={<BondViewerPage />} />
          <Route path="/licencias/florida-certificate" element={<FloridaCertificateViewerPage />} />
          {/* Videos que hacen dinero */}
          <Route path="/comunidad" element={<VideosQueHacenDinero />} />
          <Route path="/solousosettersprivado2026" element={<SetterScriptsPage />} />
          <Route path="/guiondeventaagencia" element={<VentaDirectaScriptPage />} />
          <Route path="/home-nuevo" element={<HomeDemoPage />} />
          <Route path="/demo-home" element={<HomeDemoPage />} />
          <Route path="/guiones-de-venta" element={<VentaDirectaScriptPage />} />
          <Route path="/telepro" element={<TelepromptrPage />} />
          <Route path="/plandecompensacion" element={<PlanCompensacionRedirect />} />
          <Route path="/avisocodigos" element={<AvisoCodigosPage />} />
          <Route path="/protocolo-2026" element={<Protocolo2026Page />} />
          <Route path="/curso/videos" element={<CursoVideosPage />} />
          <Route path="/analisisfinanciero" element={<AnalisisFinancieroLanding />} />
          <Route path="/analisis-financiero" element={<AnalisisFinancieroLanding />} />
          <Route path="/analisisfinanciero/simulador" element={<AnalisisFinancieroPage />} />
          <Route path="/analisisfinanciero/certificados" element={<CertificadosSimuladorPage />} />
          <Route path="/negocio-certificados" element={<CertificadosSimuladorPage />} />
          <Route path="/analisisfinanciero/club-vacacional" element={<ClubVacacionalSimuladorPage />} />
          <Route path="/negocio-club-vacacional" element={<ClubVacacionalSimuladorPage />} />
          <Route path="/inversores" element={<InversoresRedirect />} />
          <Route path="/sociosfundadores" element={<SociosFundadoresRedirect />} />
          <Route path="/socios-fundadores" element={<SociosFundadoresRedirect />} />
          <Route path="/kit-inversor" element={<KitInversorRedirect />} />
          <Route path="/kitinversor" element={<KitInversorRedirect />} />
          <Route path="/kit-inversionista" element={<KitInversorRedirect />} />
          <Route path="/cursoclubvacacional" element={<CursoClubVacacionalRedirect />} />
          <Route path="/curso-club-vacacional" element={<CursoClubVacacionalRedirect />} />
          <Route path="/cursovisaconexito" element={<CursoVisaConExitoRedirect />} />
          <Route path="/curso-visa-con-exito" element={<CursoVisaConExitoRedirect />} />
          <Route path="/cursoelpaquetedelmillon" element={<CursoElPaqueteDelMillonRedirect />} />
          <Route path="/curso-el-paquete-del-millon" element={<CursoElPaqueteDelMillonRedirect />} />
          <Route path="/cursocertificadosdeviaje" element={<CursoElPaqueteDelMillonRedirect />} />
          <Route path="/cursoreferidos" element={<CursoReferidosRedirect />} />
          <Route path="/curso-referidos" element={<CursoReferidosRedirect />} />
          <Route path="/cursoembajador" element={<CursoReferidosRedirect />} />
          <Route path="/cursoacademiab2b" element={<CursoAcademiaB2BRedirect />} />
          <Route path="/curso-academia-b2b" element={<CursoAcademiaB2BRedirect />} />
          <Route path="/cursoplataformasb2b" element={<CursoAcademiaB2BRedirect />} />
          <Route path="/academia" element={<AcademiaPage />} />
          <Route path="/salesfirst" element={<SalesFirstPage />} />
          <Route path="/sales-first" element={<SalesFirstPage />} />
          <Route path="/hotelpromo" element={<HotelPromoPage />} />
          <Route path="/hotel-promo" element={<HotelPromoPage />} />
          <Route path="/propuesta-hotel" element={<HotelPromoPage />} />
          <Route path="/evoluciondelsistemacv" element={<EvolucionSistemaPage />} />
          <Route path="/evolucion-del-sistema" element={<EvolucionSistemaPage />} />
          <Route path="/evolucion" element={<EvolucionSistemaPage />} />
          <Route path="/opc" element={<OpcPage />} />
          <Route path="/businessrelief" element={<BusinessReliefRedirect />} />
          <Route path="/business-relief" element={<BusinessReliefRedirect />} />
          <Route path="/aliviodenegocio" element={<BusinessReliefRedirect />} />
          <Route path="/tonsofleads" element={<TonsOfLeadsPage />} />
          <Route path="/tons-of-leads" element={<TonsOfLeadsPage />} />
          <Route path="/cursotonsofleads" element={<TonsOfLeadsPage />} />
          <Route path="/proyecto-opc" element={<OpcPage />} />
          <Route path="/proyectoopc" element={<OpcPage />} />
          <Route path="/opc/terminos" element={<OpcTerminosPage />} />
          <Route path="/opc-terminos" element={<OpcTerminosPage />} />
          <Route path="/terminos-opc" element={<OpcTerminosPage />} />
          <Route path="/cenaconcarlos" element={<CenaConCarlosRedirect />} />
          <Route path="/cena-con-carlos" element={<CenaConCarlosRedirect />} />
          <Route path="/cena" element={<CenaConCarlosRedirect />} />

          <Route path="/registrosopc" element={<RegistrosOpcRedirect />} />
          <Route path="/registro-tours" element={<RegistrosOpcRedirect />} />
          <Route path="/registrotours" element={<RegistrosOpcRedirect />} />
          <Route path="/cursos" element={<AcademiaPage />} />
          <Route path="/cursovideoacademy" element={<CursoVideoAcademyRedirect />} />
          <Route path="/curso-video-academy" element={<CursoVideoAcademyRedirect />} />
          <Route path="/videoacademy" element={<CursoVideoAcademyRedirect />} />
          <Route path="/cursocierremaestro" element={<CursoCierreMaestroRedirect />} />
          <Route path="/curso-cierre-maestro" element={<CursoCierreMaestroRedirect />} />
          <Route path="/cierremaestro" element={<CursoCierreMaestroRedirect />} />
          <Route path="/cursokitarranque" element={<CursoKitArranqueRedirect />} />
          <Route path="/curso-kit-arranque" element={<CursoKitArranqueRedirect />} />
          <Route path="/kitarranque" element={<CursoKitArranqueRedirect />} />
          <Route path="/pasosiniciales" element={<CursoKitArranqueRedirect />} />
          <Route path="/linksdepago" element={<PaymentLinksPage />} />
          <Route path="/linksdepago2026" element={<PaymentLinksPage />} />
          <Route path="*" element={<Dashboard />} />
        </Routes>
        <ConditionalFloatingWhatsApp />
        <ConditionalAAFooter />
      </BrowserRouter>
    </div>
  );
}

function ConditionalAAHeader() {
  const { pathname } = useLocation();
  if (pathname === "/planes" || pathname === "/salesfirst" || pathname === "/sales-first") return null;
  return <AAHeaderBanner />;
}

function ConditionalLangBanner() {
  const { pathname } = useLocation();
  if (pathname === "/salesfirst" || pathname === "/sales-first") return null;
  return <LanguagePromptBanner />;
}

function ConditionalFloatingWhatsApp() {
  const { pathname } = useLocation();
  if (pathname === "/salesfirst" || pathname === "/sales-first") return null;
  return <FloatingWhatsApp />;
}

function ConditionalAAFooter() {
  const { pathname } = useLocation();
  // En el home el banner de American Airlines se renderiza antes del footer.
  if (pathname === "/" || pathname === "/planes" || pathname === "/salesfirst" || pathname === "/sales-first") return null;
  return <AAFooterBanner />;
}

export default App;
